import { useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { catalogueReportPeriod, catalogueUsageGroups } from "@reminder-tracker/backend-contracts";
import type { CatalogueMonthlyReport } from "@reminder-tracker/backend-contracts";
import { getCatalogueMonthlyReport } from "../../backend/functions";
import { classifyCatalogueFailure } from "./failure";
import {
  AdministrationAuditDetails,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

const messages: Record<CatalogueMonthlyReport["state"], string> = {
  "not-collected": "Collection was not active for this month. No usage figures are available.",
  open: "This month is still open. Its fixed report will be prepared after the month ends.",
  preparing: "This report is being prepared. Check again after the daily reporting run.",
  incomplete: "Collection is incomplete for this month. Missing information is not shown as zero.",
  ready: "This is a fixed monthly report of confirmed reminder saves.",
  expired: "This report has reached the end of its retention period and is no longer available.",
};
const breakdowns = [
  ["overview", "Creation overview"],
  ["market:", "Catalogue market"],
  ["category:", "Broad category"],
  ["timing:", "Timing choices"],
] as const;
function previousMonth() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
    .toISOString()
    .slice(0, 7);
}
function date(value: number) {
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "long", timeZone: "UTC" }).format(value);
}

/** Operate: a month and one ruled breakdown, extending the existing catalogue section. */
export function CatalogueReports({ onClose }: { onClose?: () => void }): React.JSX.Element {
  const read = useAction(getCatalogueMonthlyReport);
  const [period, setPeriod] = useState(previousMonth);
  const [breakdown, setBreakdown] = useState("overview");
  const [report, setReport] = useState<CatalogueMonthlyReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [securityFailure, setSecurityFailure] = useState<Error | null>(null);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  async function load(download = false) {
    if (busyRef.current) return;
    try {
      catalogueReportPeriod(period);
    } catch {
      setMessage("Choose a valid reporting month.");
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setMessage(null);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await Promise.race([
        read({ period, correlationId: `corr_${crypto.randomUUID().replaceAll("-", "")}` }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error("Report timed out")), 15_000);
        }),
      ]);
      if (!mounted.current) return;
      setReport(result);
      if (download && (result.state === "ready" || result.state === "incomplete")) {
        // Fresh authorization above; export exactly the same protected release, never cached raw data.
        const { correlationId: _correlationId, ...safeReport } = result;
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(safeReport, null, 2)], { type: "application/json" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `catalogue-usage-${result.environment}-${result.period}.json`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        setMessage("The protected report is ready to download.");
      }
    } catch (error) {
      if (!mounted.current) return;
      setReport(null);
      if (classifyCatalogueFailure(error).kind === "security")
        setSecurityFailure(
          Object.assign(new Error("Verify your administrator access before continuing."), {
            data: { code: "REAUTHENTICATION_REQUIRED" },
          }),
        );
      else setMessage("We couldn't load this report. Check the month and try again.");
    } finally {
      clearTimeout(timer);
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  if (securityFailure !== null) throw securityFailure;
  const shown =
    report?.cells.filter((cell) =>
      breakdown === "overview"
        ? cell.group === "created:all" || cell.group.startsWith("origin:")
        : cell.group.startsWith(breakdown),
    ) ?? [];
  return (
    <div className="catalogue-reports">
      {onClose ? (
        <button type="button" onClick={onClose} disabled={busy}>
          Back to catalogue
        </button>
      ) : null}
      <AdministrationWorkSurface
        id="monthly-catalogue-usage"
        title="Monthly catalogue usage"
        purpose="Review a fixed monthly count of confirmed reminder saves."
        state={{
          label:
            report === null
              ? "Choose a reporting month"
              : report.state === "ready"
                ? "Report ready"
                : report.state === "incomplete"
                  ? "Incomplete report"
                  : "Report not ready",
          tone: report?.state === "ready" ? "healthy" : report === null ? "neutral" : "monitor",
        }}
        consequence="This report supports catalogue planning. It does not measure completed renewals or prove that a suggested timing was effective."
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void load();
          }}
        >
          <label>
            Reporting month
            <input
              type="month"
              value={period}
              disabled={busy}
              required
              onChange={(event) => {
                setPeriod(event.target.value);
                setReport(null);
                setMessage(null);
              }}
            />
          </label>
          <button type="submit" disabled={busy}>
            {busy ? "Loading report…" : "Show report"}
          </button>
        </form>
        {message === null ? null : <p role="status">{message}</p>}
        {busy ? <p role="status">Checking reporting access and loading the fixed report…</p> : null}
        {report === null ? null : (
          <>
            <h4>
              {new Intl.DateTimeFormat("en-GB", {
                month: "long",
                year: "numeric",
                timeZone: "UTC",
              }).format(catalogueReportPeriod(report.period).startsAt)}
            </h4>
            <AdministrationAuditDetails summary="Reporting basis and metric version">
              <p>Reporting periods use UTC.</p>
              <p>
                {report.environment === "local"
                  ? "Local synthetic test data"
                  : "Shared Test synthetic data"}{" "}
                · {report.metricVersion}
              </p>
            </AdministrationAuditDetails>
            <p role="status">{messages[report.state]}</p>
            {report.state === "ready" || report.state === "incomplete" ? (
              <>
                <label>
                  Breakdown
                  <select
                    value={breakdown}
                    onChange={(event) => setBreakdown(event.target.value)}
                    disabled={busy}
                  >
                    {breakdowns.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                {breakdown === "market:" ? (
                  <p>Catalogue market describes the content selected, not where customers live.</p>
                ) : null}
                <p>
                  Small groups are withheld, along with related breakdowns that could reveal them.
                  Withheld values are not zero.
                </p>
                <table>
                  <caption>{breakdowns.find(([value]) => value === breakdown)?.[1]}</caption>
                  <thead>
                    <tr>
                      <th scope="col">Measure</th>
                      <th scope="col">Reminders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((cell) => (
                      <tr key={cell.group}>
                        <th scope="row">
                          {catalogueUsageGroups.find((group) => group.id === cell.group)?.label}
                        </th>
                        <td>
                          {cell.state === "reported"
                            ? new Intl.NumberFormat("en-GB").format(cell.value!)
                            : cell.state === "withheld"
                              ? "Not enough aggregate activity to report"
                              : "Incomplete"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p>Report retained until {date(report.expiresAtEpochMilliseconds)}.</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    void load(true);
                  }}
                >
                  Download protected report (JSON)
                </button>
              </>
            ) : null}
          </>
        )}
      </AdministrationWorkSurface>
    </div>
  );
}
